import {EcaseHttpService, ECaseSnackBarService, ECaseUtilsGlobal, LoginAuthenticationService, ParametersService, RefreshDataService} from 'synto-common';
import moment from 'moment';

/**
 * Nested Research Team — member consent (Send / Withdraw) inside fb-table.
 *
 * WHEN IT RUNS
 * Parent fb-team-table passes enableConsentByRoleConfig=true → fb-table creates this helper.
 *
 * FLOW 
 * 1. start() — discover field names in sub_table JSON, load role rules from API.
 * 2. GET /api/getConsentInfoByCompetition/{id} → which roles need consent + roleParentId.
 * 3. For each member row: mustAskConsent()? → show Send/Withdraw (see refreshConsentButtons).
 * 4. Send → POST /api/addOrUpdateSyntoTeamMembers (operationType 1) → Camunda → email + consent task.
 * 5. Withdraw → same API with operationType 0.
 *
 * WHO GETS CONSENT UI
 * - Role has isConsentRequired in admin AND roleParentId is not COLLABORATOR_PARENT_ROLE_ID (35).
 * - Not the outer team leader duplicated in the inner table (teamLeaderRole / isTeamLeaderRow).
 *
 * TEAM ID (one outer row = one CASE_APPLICATION_TEAM)
 * - resolveTeamContextForConsent() reuses globalPrj.caseApplicationPiTeamId / caseApplicationTeamId or parent row id.
 * - Negative parent caseApplicationTeamId (-1 placeholder) is ignored; Camunda resolves principal team.
 * - Row 0 on an existing application never sends isCreateNewTeam (avoids orphan teams 56655/56661).
 * - Send no longer overwrites parent caseApplicationTeamId when a team was already linked.
 *
 * CONSENT STATUS (globalPrj.formData.TEAM_MEMBER_CONSENTS.data)
 * - no row        → Send enabled
 * - IS_CONSENT_GIVEN = ' '  → request pending (space char, not empty)
 * - 'true' / 'false' → approved / refused text on the button
 *
 * FIELD DISCOVERY
 * - role select: LOV 12 or memberConsentFields.roleSelect
 * - send/withdraw: subBlocks buttons or defaults button_send_approval_request / button_withdraw_request
 * - SSO autocomplete + name/email: auto-detect or memberConsentFields overrides
 */

/** Term.TERM_PARENT_ID for "Collaborator (without user account)" — no consent workflow for these roles. */
export const COLLABORATOR_PARENT_ROLE_ID = 35;

/** Default row keys — aligned with fb-team-table teamLeaderConsent (no FormBuilder button blocks required). */
export const MEMBER_CONSENT_SEND_BUTTON_NAME = 'button_send_approval_request';
export const MEMBER_CONSENT_WITHDRAW_BUTTON_NAME = 'button_withdraw_request';

/** Optional overrides on sub_table block in FormBuilder JSON */
export interface MemberConsentFieldOverrides {
  roleSelect?: string;
  userAutocomplete?: string;
  sendButton?: string;
  withdrawButton?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface ResolvedConsentFields {
  roleSelect?: string;
  userAutocomplete?: string;
  sendButton?: string;
  withdrawButton?: string;
  collaborator?: { firstName?: string; lastName?: string; email?: string };
}

export interface CompetitionRoleRule {
  roleParentId: number;
  isConsentRequired: boolean;
}

export type CompetitionRoleRulesByRoleId = Record<number, CompetitionRoleRule>;

export interface FbTableMemberConsentHost {
  globalPrj: any;
  globalConf: any;
  confChange: any;
  bphChange: any;
  selectedSectionId: string;
  selectedLanguage: string;
  teamTableBlockName?: string;
  teamTableIndex?: number;
  teamTableNestedTableBlockName?: string;
  /** From parent TeamTable — used to hide nested consent for the outer team leader row. */
  teamLeaderRole?: number;
  teamLeaderComponent?: string;
}

export class FbTableMemberConsentHelper {

  roleRules: CompetitionRoleRulesByRoleId = {};
  private fields: ResolvedConsentFields = {};

  constructor(
    private host: FbTableMemberConsentHost,
    private http: EcaseHttpService,
    private snackBar: ECaseSnackBarService,
    private refreshDataService: RefreshDataService,
    private loginAuth: LoginAuthenticationService,
    private parameterService: ParametersService,
  ) {}

  start(): void {
    this.fields = this.resolveFieldsFromBlockConfig();
    this.exposeRefreshForFormBuilderActions();
    this.connectRoleSelectOnChange();
    this.connectUserAutocompleteOnChange();
    this.loadRoleRulesFromServer();
  }

  /** Keep nested table rows in sync when parent reassigns bphChange after save. */
  setBphChange(bphChange: any): void {
    if (bphChange) {
      this.host.bphChange = bphChange;
    }
  }

  refreshAllRows(): void {
    this.host.bphChange?.rows?.forEach((row) => {
      this.applyMemberRowFields(row);
      this.refreshConsentButtons(row);
    });
  }

  /** Show/fill identity fields by role (account vs collaborator without SSO). Never hides filled name/email in account mode. */
  applyMemberRowFields(row: any): void {
    if (!row) {
      return;
    }
    const isManual = this.isCollaboratorWithoutAccount(row);
    const acName = this.fields.userAutocomplete;
    if (acName && row[acName]) {
      this.setFieldVisible(row[acName], !isManual);
    }
    const identity = this.fields.collaborator;
    if (!identity) {
      return;
    }
    [identity.firstName, identity.lastName, identity.email].forEach((fieldName) => {
      if (!fieldName || !row[fieldName]) {
        return;
      }
      this.setFieldVisible(row[fieldName], true);
      if (isManual) {
        row[fieldName].disabled = false;
      } else if (this.getSsoUserId(row)) {
        row[fieldName].disabled = true;
      }
    });
    if (!isManual) {
      this.fillMemberIdentityFromUser(row);
    }
  }

  /** At least one role in this competition requires member consent (from getConsentInfoByCompetition). */
  hasAnyConsentRequiredRole(): boolean {
    return Object.values(this.roleRules).some((rule) => rule.isConsentRequired);
  }

  getSendButtonFieldName(): string | undefined {
    return this.fields.sendButton;
  }

  getWithdrawButtonFieldName(): string | undefined {
    return this.fields.withdrawButton;
  }

  /** Show Send/Withdraw (or consent status text) for this member row. */
  shouldShowConsentUiForRow(row: any): boolean {
    const sendName = this.fields.sendButton;
    if (!sendName) {
      return false;
    }
    const send = row?.[sendName];
    if (!send) {
      return false;
    }
    return !send.notApplicable || !!send.isShowInfoText;
  }

  refreshConsentButtons(row: any): void {
    if (!this.fields.sendButton || !this.fields.withdrawButton) {
      return;
    }

    this.createButtonsIfMissing(row);
    const send = row[this.fields.sendButton];
    const withdraw = row[this.fields.withdrawButton];

    const showButtons = this.mustAskConsent(row);
    send.notApplicable = !showButtons;
    withdraw.notApplicable = !showButtons;

    if (!showButtons) {
      send.isShowInfoText = false;
      withdraw.isShowInfoText = false;
      send.isDisabled = true;
      withdraw.isDisabled = true;
      return;
    }

    const ssoUserId = this.getSsoUserId(row);
    const roleId = this.getRoleId(row);
    const consentList = this.host.globalPrj?.formData?.TEAM_MEMBER_CONSENTS?.data;

    if (!ssoUserId || !roleId || !Array.isArray(consentList)) {
      send.isDisabled = false;
      withdraw.isDisabled = true;
      return;
    }

    const consent = consentList.find((item: any) =>
      Number(item.SSO_USER_ID) === ssoUserId && Number(item.TEAM_MEMBER_ROLE_ID) === roleId
    );

    if (!consent) {
      if (send.isClicked && !send.isShowInfoText) {
        this.applyPendingRequestButtonState(row);
        return;
      }
      send.isDisabled = false;
      withdraw.isDisabled = true;
      return;
    }

    this.applyConsentStatusToButtons(row, consent, ssoUserId);
  }

  sendApproval(row: any, rowIndex: number): void {
    if (!this.fields.sendButton) {
      return;
    }

    if (!this.mustAskConsent(row)) {
      return;
    }

    const roleId = this.getRoleId(row);
    if (!roleId) {
      this.snackBar.show('failure', this.host.selectedLanguage === 'en'
        ? 'Select a team member role before sending consent.'
        : 'Sélectionnez un rôle avant d\'envoyer le consentement.');
      return;
    }

    if (!this.isCollaboratorWithoutAccount(row) && !this.getSsoUserId(row)) {
      this.snackBar.show('failure', this.host.selectedLanguage === 'en'
        ? 'Select a team member before sending consent.'
        : 'Sélectionnez un membre avant d\'envoyer le consentement.');
      return;
    }

    const parentTeamRow = this.getParentTeamRow();
    const teamContext = this.resolveTeamContextForConsent(parentTeamRow);
    const memberPayload: any = {
      roleId,
      isReadWriteAccess: 902,
      operationType: 1,
      caseApplicationTeamId: teamContext.teamId,
      isCreateNewTeam: teamContext.isCreateNewTeam,
    };

    if (this.isCollaboratorWithoutAccount(row)) {
      this.appendCollaboratorToPayload(row, memberPayload);
    } else {
      memberPayload.teamMemberSsoUserId = this.getSsoUserId(row);
    }

    const send = row[this.fields.sendButton];
    send.isDisabled = true;
    send.isValid = false;
    send.isClicked = true;
    if (this.fields.withdrawButton) {
      row[this.fields.withdrawButton].isDisabled = false;
    }

    this.http.post('/api/addOrUpdateSyntoTeamMembers', this.buildRequest([memberPayload])).subscribe({
      next: (response: any) => {
        const ok = Number(response?.status) === 200 || response?.status === true;
        if (ok) {
          if (parentTeamRow) {
            const hadPersistedTeamId = this.normalizeTeamId(parentTeamRow.caseApplicationTeamId) != null;
            const resolvedTeamId = this.normalizeTeamId(response?.caseApplicationTeamId)
              ?? teamContext.teamId
              ?? this.resolvePrincipalTeamIdFromPrj();
            if (!hadPersistedTeamId && resolvedTeamId) {
              this.syncParentTeamId(parentTeamRow, resolvedTeamId);
            }
          }
          this.markConsentRequestPending(row);
          this.showMessage('Request sent', 'Demande envoyée');
          this.saveRowToProject(row, rowIndex);
        } else {
          this.snackBar.show('failure', response?.errorMessage || response?.message || 'Error occurred');
          send.isDisabled = false;
          send.isClicked = false;
          if (this.fields.withdrawButton) {
            row[this.fields.withdrawButton].isDisabled = true;
          }
        }
      },
      error: (err) => {
        const body = err?.error;
        this.snackBar.show('failure', body?.errorMessage || body?.message || 'Error occurred');
        send.isDisabled = false;
        send.isClicked = false;
        if (this.fields.withdrawButton) {
          row[this.fields.withdrawButton].isDisabled = true;
        }
      }
    });
  }

  withdrawApproval(row: any, rowIndex: number): void {
    if (!this.fields.sendButton || !this.fields.withdrawButton) {
      return;
    }

    const parentTeamRow = this.getParentTeamRow();
    const teamContext = this.resolveTeamContextForConsent(parentTeamRow);
    const send = row[this.fields.sendButton];
    const withdraw = row[this.fields.withdrawButton];

    const memberPayload = {
      roleId: this.getRoleId(row),
      teamMemberSsoUserId: this.getSsoUserId(row),
      isReadWriteAccess: 902,
      operationType: 0,
      caseApplicationTeamId: teamContext.teamId,
    };

    withdraw.isDisabled = true;
    send.isValid = false;
    send.isClicked = false;
    withdraw.isDisabled = true;
    send.isDisabled = false;
    send.isShowInfoText = false;
    send.infoText = {};
    row.rowDeleteButtonHide = false;
    row.rowEditButtonHide = false;

    this.http.post('/api/addOrUpdateSyntoTeamMembers', this.buildRequest([memberPayload])).subscribe({
      next: (response: any) => {
        const ok = Number(response?.status) === 200 || response?.status === true;
        if (ok) {
          this.removePendingConsentRecord(row);
          this.showMessage('Request withdrawn', 'Demande retirée');
          this.saveRowToProject(row, rowIndex);
        } else {
          this.snackBar.show('failure', response?.errorMessage || response?.message || 'Error occurred');
          this.refreshConsentButtons(row);
        }
      },
      error: (err) => {
        const body = err?.error;
        this.snackBar.show('failure', body?.errorMessage || body?.message || 'Error occurred');
        this.refreshConsentButtons(row);
      },
    });
  }

  /**
   * Field names from sub_table config; send/withdraw fall back to team-table defaults when absent.
   */
  private resolveFieldsFromBlockConfig(): ResolvedConsentFields {
    const blocks: any[] = this.host.confChange?.subBlocks || [];
    const overrides: MemberConsentFieldOverrides = this.host.confChange?.memberConsentFields || {};
    const teamMemberRoleLov = ECaseUtilsGlobal.APPLICATION_TEAM_MEMBER_ROLE_TAXONOMY_ID;

    const roleSelect = overrides.roleSelect
      || blocks.find((b) => b.type === 'select' && Number(b.listName) === teamMemberRoleLov)?.name;

    const sendButton = overrides.sendButton
      || blocks.find((b) => b.type === 'button' && /send.*approval/i.test(b.name || ''))?.name
      || MEMBER_CONSENT_SEND_BUTTON_NAME;

    const withdrawButton = overrides.withdrawButton
      || blocks.find((b) => b.type === 'button' && /withdraw/i.test(b.name || ''))?.name
      || MEMBER_CONSENT_WITHDRAW_BUTTON_NAME;

    const userAutocomplete = overrides.userAutocomplete
      || blocks.find((b) => b.type === 'AutoComplete')?.name;

    const collaborator = {
      firstName: overrides.firstName || this.findTextFieldByNamePattern(blocks, [/first_name/i, /member_first/i]),
      lastName: overrides.lastName || this.findTextFieldByNamePattern(blocks, [/last_name/i, /member_last/i, /member_name/i]),
      email: overrides.email || this.findTextFieldByNamePattern(blocks, [/member_email/i, /^e-?mail$/i, /_email$/i]),
    };

    return {roleSelect, sendButton, withdrawButton, userAutocomplete, collaborator};
  }

  private loadRoleRulesFromServer(): void {
    const competitionId = this.host.globalPrj?.cpmCompetitionID;
    if (!competitionId) {
      this.refreshAllRows();
      return;
    }

    this.http.get(`/api/getConsentInfoByCompetition/${competitionId}`).subscribe((response: any) => {
      this.roleRules = {};
      if (response?.status && Array.isArray(response.result)) {
        response.result.forEach((item: any) => {
          this.roleRules[Number(item.role)] = {
            roleParentId: Number(item.roleParentId),
            isConsentRequired: item.isConsentmentRequired === true || item.isConsentmentRequired === 'Y',
          };
        });
      }
      this.refreshAllRows();
    });
  }

  private connectRoleSelectOnChange(): void {
    const fieldName = this.fields.roleSelect;
    if (!fieldName) {
      return;
    }
    const roleBlock = this.host.confChange?.subBlocks?.find((block: any) => block.name === fieldName);
    if (!roleBlock) {
      return;
    }
    const helper = this;
    roleBlock.enableOutputEvent = true;
    const existing = roleBlock.updateOutputEventFunctions || [];
    if (!existing.includes('onMemberRoleChange')) {
      roleBlock.updateOutputEventFunctions = [...existing, 'onMemberRoleChange'];
    }
    roleBlock.onMemberRoleChange = function(_value: any, rowIndex: number) {
      const row = helper.host.bphChange?.rows?.[rowIndex];
      if (row) {
        helper.applyMemberRowFields(row);
        helper.refreshConsentButtons(row);
      }
    };
  }

  private connectUserAutocompleteOnChange(): void {
    const fieldName = this.fields.userAutocomplete;
    if (!fieldName) {
      return;
    }
    const userBlock = this.host.confChange?.subBlocks?.find((block: any) => block.name === fieldName);
    if (!userBlock) {
      return;
    }
    const helper = this;
    userBlock.enableOutputEvent = true;
    userBlock.updateOutputEventFunctions = ['onMemberUserChange'];
    userBlock.onMemberUserChange = function(_value: any, rowIndex: number) {
      const row = helper.host.bphChange?.rows?.[rowIndex];
      if (row) {
        helper.applyMemberRowFields(row);
        helper.refreshConsentButtons(row);
      }
    };
  }

  private exposeRefreshForFormBuilderActions(): void {
    const helper = this;
    this.host.globalConf.refreshMemberConsentForRow = function(_value: any, rowIndex: number) {
      const row = helper.host.bphChange?.rows?.[rowIndex];
      if (row) {
        helper.applyMemberRowFields(row);
        helper.refreshConsentButtons(row);
      }
    };
  }

  private findTextFieldByNamePattern(blocks: any[], patterns: RegExp[]): string | undefined {
    const block = blocks.find((b) => b.type === 'text' && patterns.some((p) => p.test(b.name || '')));
    return block?.name;
  }

  private setFieldVisible(fieldState: any, visible: boolean): void {
    fieldState.hide = !visible;
    fieldState.globalShowDetail = visible;
    fieldState.hideCell = !visible;
  }

  /** Copy first/last/email from autocomplete SQL row into text fields (table + detail). */
  private fillMemberIdentityFromUser(row: any): void {
    const acName = this.fields.userAutocomplete;
    const identity = this.fields.collaborator;
    if (!acName || !identity || !row[acName]) {
      return;
    }
    const data = row[acName]?.value?.data;
    if (!data || typeof data !== 'object') {
      return;
    }
    const firstName = this.pickDataField(data, ['first_name', 'FIRST_NAME', 'firstName', 'given_name']);
    const lastName = this.pickDataField(data, ['last_name', 'LAST_NAME', 'lastName', 'family_name', 'FAMILY_NAME', 'surname']);
    const email = this.pickDataField(data, ['email', 'EMAIL', 'username', 'USER_EMAIL', 'user_email']);

    if (identity.firstName && row[identity.firstName] && firstName !== undefined) {
      row[identity.firstName].value = firstName;
    }
    if (identity.lastName && row[identity.lastName] && lastName !== undefined) {
      row[identity.lastName].value = lastName;
    }
    if (identity.email && row[identity.email] && email !== undefined) {
      row[identity.email].value = email;
    }
  }

  private pickDataField(data: any, keys: string[]): string | undefined {
    for (const key of keys) {
      const v = data[key];
      if (v !== undefined && v !== null && String(v).trim() !== '') {
        return String(v).trim();
      }
    }
    return undefined;
  }

  private mustAskConsent(row: any): boolean {
    if (this.isNestedTeamLeaderRow(row) || !this.fields.roleSelect) {
      return false;
    }
    const roleId = this.getRoleId(row);
    const rule = roleId ? this.roleRules[roleId] : undefined;
    if (!rule || rule.roleParentId === COLLABORATOR_PARENT_ROLE_ID) {
      return false;
    }
    return rule.isConsentRequired;
  }

  private isCollaboratorWithoutAccount(row: any): boolean {
    const roleId = this.getRoleId(row);
    return !!roleId && this.roleRules[roleId]?.roleParentId === COLLABORATOR_PARENT_ROLE_ID;
  }

  private getRoleId(row: any): number | undefined {
    const fieldName = this.fields.roleSelect;
    if (!fieldName) {
      return undefined;
    }
    const value = row?.[fieldName]?.value?.value;
    if (value === undefined || value === null || value === '') {
      return undefined;
    }
    return Number(value);
  }

  private getSsoUserId(row: any): number | undefined {
    const fieldName = this.fields.userAutocomplete;
    if (!fieldName) {
      return undefined;
    }
    const value = row?.[fieldName]?.value?.value;
    if (value === undefined || value === null || value === '') {
      return undefined;
    }
    return Number(value);
  }

  private appendCollaboratorToPayload(row: any, payload: any): void {
    const c = this.fields.collaborator;
    if (!c) {
      return;
    }
    if (c.firstName && row[c.firstName]) {
      payload.collaboratorFirstName = row[c.firstName].value;
    }
    if (c.lastName && row[c.lastName]) {
      payload.collaboratorLastName = row[c.lastName].value;
    }
    if (c.email && row[c.email]) {
      payload.collaboratorEmailAddress = row[c.email].value;
    }
  }

  private getParentTeamRow(): any {
    const {teamTableBlockName, teamTableIndex, selectedSectionId} = this.host;
    if (!teamTableBlockName || teamTableIndex === undefined || teamTableIndex === null) {
      return null;
    }
    return this.host.globalPrj?.[selectedSectionId]?.[teamTableBlockName]?.rows?.[teamTableIndex];
  }

  /**
   * Consent for team leader is on the outer TeamTable only (teamLeaderConsent).
   * Inner row: isTeamLeaderRow flag and/or same SSO + teamLeaderRole as outer leader.
   */
  private isNestedTeamLeaderRow(row: any): boolean {
    if (row?.isTeamLeaderRow) {
      return true;
    }
    if (this.isSameAsOuterTeamLeader(row)) {
      row.isTeamLeaderRow = true;
      return true;
    }
    return false;
  }

  private isSameAsOuterTeamLeader(row: any): boolean {
    const parentRow = this.getParentTeamRow();
    if (!parentRow) {
      return false;
    }
    const {teamLeaderComponent, teamLeaderRole} = this.getOuterTeamTableLeaderConfig();
    if (!teamLeaderRole) {
      return false;
    }
    const outerSso = this.getSsoUserIdFromField(parentRow, teamLeaderComponent);
    const innerSso = this.getSsoUserId(row);
    const innerRole = this.getRoleId(row);
    if (!outerSso || !innerSso || !innerRole) {
      return false;
    }
    return outerSso === innerSso && innerRole === teamLeaderRole;
  }

  private getOuterTeamTableLeaderConfig(): { teamLeaderComponent: string; teamLeaderRole?: number } {
    const blockName = this.host.teamTableBlockName;
    const sectionConf = blockName ? this.host.globalConf?.[this.host.selectedSectionId]?.[blockName] : undefined;
    const teamLeaderComponent = this.host.teamLeaderComponent
      || sectionConf?.teamLeaderComponent
      || 'autocomplete_leader_name';
    const roleRaw = this.host.teamLeaderRole ?? sectionConf?.teamLeaderRole;
    const teamLeaderRole = roleRaw !== undefined && roleRaw !== null && roleRaw !== ''
      ? Number(roleRaw)
      : undefined;
    return {teamLeaderComponent, teamLeaderRole};
  }

  private getSsoUserIdFromField(row: any, fieldName: string): number | undefined {
    const value = row?.[fieldName]?.value?.value;
    if (value === undefined || value === null || value === '') {
      return undefined;
    }
    return Number(value);
  }

  private createButtonsIfMissing(row: any): void {
    const sendName = this.fields.sendButton;
    const withdrawName = this.fields.withdrawButton;
    if (sendName && !row[sendName]) {
      row[sendName] = {label: {en: 'Send request', fr: 'Envoyer la demande'}, isDisabled: false};
    }
    if (withdrawName && !row[withdrawName]) {
      row[withdrawName] = {label: {en: 'Withdraw request', fr: 'Retirer la demande'}, isDisabled: true};
    }
  }

  private applyConsentStatusToButtons(row: any, consent: any, ssoUserId: number): void {
    const send = row[this.fields.sendButton];
    const withdraw = row[this.fields.withdrawButton];
    const status = consent.IS_CONSENT_GIVEN;
    const dateFormat = this.parameterService.parameter['/core/ui/default_date/format'];
    const formattedDate = moment(consent.DATE_OF_CONSENT).format(dateFormat.toUpperCase());
    const taskId = consent.TASK_ID;
    const viewUrl = `${this.loginAuth.baseUrl}/api/viewTeamMemberForm/${this.host.globalPrj.applicationId}/${ssoUserId}/${taskId}`;

    if (status === 'true') {
      send.infoText = {
        en: `Date of approval: ${formattedDate} <a target='_blank' href='${viewUrl}'>View</a>`,
        fr: `Date de l'approbation: ${formattedDate} <a target='_blank' href='${viewUrl}'>Afficher</a>`,
      };
      send.isValid = true;
      send.isShowInfoText = true;
      send.isDisabled = true;
      withdraw.isDisabled = true;
      row.rowDeleteButtonHide = false;
      return;
    }

    if (status === 'false') {
      send.infoText = {
        en: `Date of refusal: ${formattedDate}`,
        fr: `Date de refus: ${formattedDate}`,
      };
      send.isValid = false;
      send.isShowInfoText = true;
      send.isDisabled = true;
      withdraw.isDisabled = false;
      row.rowDeleteButtonHide = true;
      return;
    }

    if (status === ' ') {
      this.applyPendingRequestButtonState(row);
    }
  }

  /** After Send request succeeds — matches Salim row state until page reload refreshes TEAM_MEMBER_CONSENTS from server. */
  private markConsentRequestPending(row: any): void {
    this.applyPendingRequestButtonState(row);
    this.upsertPendingConsentRecord(row);
  }

  private applyPendingRequestButtonState(row: any): void {
    const send = row[this.fields.sendButton];
    const withdraw = row[this.fields.withdrawButton];
    if (!send || !withdraw) {
      return;
    }
    send.isDisabled = true;
    send.isValid = false;
    send.isClicked = true;
    send.isShowInfoText = false;
    send.infoText = {};
    withdraw.isDisabled = false;
    row.rowDeleteButtonHide = true;
  }

  private upsertPendingConsentRecord(row: any): void {
    const ssoUserId = this.getSsoUserId(row);
    const roleId = this.getRoleId(row);
    if (!ssoUserId || !roleId) {
      return;
    }
    if (!this.host.globalPrj.formData) {
      this.host.globalPrj.formData = {};
    }
    if (!this.host.globalPrj.formData.TEAM_MEMBER_CONSENTS?.data) {
      this.host.globalPrj.formData.TEAM_MEMBER_CONSENTS = {data: []};
    }
    const list: any[] = this.host.globalPrj.formData.TEAM_MEMBER_CONSENTS.data;
    const existing = list.find((item: any) =>
      Number(item.SSO_USER_ID) === ssoUserId && Number(item.TEAM_MEMBER_ROLE_ID) === roleId
    );
    if (existing) {
      existing.IS_CONSENT_GIVEN = ' ';
      existing.DATE_OF_CONSENT = new Date().toISOString();
    } else {
      list.push({
        SSO_USER_ID: ssoUserId,
        TEAM_MEMBER_ROLE_ID: roleId,
        IS_CONSENT_GIVEN: ' ',
        DATE_OF_CONSENT: new Date().toISOString(),
        TASK_ID: null,
      });
    }
  }

  private removePendingConsentRecord(row: any): void {
    const ssoUserId = this.getSsoUserId(row);
    const roleId = this.getRoleId(row);
    const list = this.host.globalPrj?.formData?.TEAM_MEMBER_CONSENTS?.data;
    if (!ssoUserId || !roleId || !Array.isArray(list)) {
      return;
    }
    this.host.globalPrj.formData.TEAM_MEMBER_CONSENTS.data = list.filter((item: any) =>
      !(Number(item.SSO_USER_ID) === ssoUserId && Number(item.TEAM_MEMBER_ROLE_ID) === roleId)
    );
  }

  private buildRequest(memberRows: any[]): any {
    const caseApplicationId = this.resolveCaseApplicationId();
    const caseProjectId = this.resolveCaseProjectId();
    return {
      rows: memberRows,
      taskId: this.host.globalPrj.taskId,
      caseApplicationId,
      applicationId: caseApplicationId,
      caseProjectId,
      projectId: caseProjectId,
      cpmCompetitionId: this.host.globalPrj.cpmCompetitionID,
      createdBy: this.host.globalPrj.createdBy?.toString(),
      isAmdPaReport: !!(this.host.globalPrj.paReportId && this.host.globalPrj.paReportId > 0),
    };
  }

  /** Prefer applicationId when caseApplicationId in prj is stale (e.g. -9999 or missing). */
  private resolveCaseApplicationId(): number {
    const prj = this.host.globalPrj;
    const candidates = [prj.applicationId, prj.caseApplicationId]
      .map((v) => Number(v))
      .filter((v) => !Number.isNaN(v) && v > 0 && v !== -9999);
    return candidates[0] ?? Number(prj.caseApplicationId);
  }

  private resolveCaseProjectId(): number | undefined {
    const prj = this.host.globalPrj;
    const windowId = Number(this.http.windowId);
    const candidates = [prj.caseProjectId, prj.projectId, windowId]
      .map((v) => Number(v))
      .filter((v) => !Number.isNaN(v) && v > 0);
    return candidates[0];
  }

  private normalizeTeamId(teamId: any): number | undefined {
    const id = Number(teamId);
    if (Number.isNaN(id) || id <= 0) {
      return undefined;
    }
    return id;
  }

  /**
   * One outer research_teams_table row → one CASE_APPLICATION_TEAM.
   * Reuse an existing team id; create a new team only when this row has none yet.
   */
  private resolveTeamContextForConsent(parentTeamRow: any | null): {
    teamId: number | undefined;
    isCreateNewTeam: boolean;
  } {
    const rowIndex = this.host.teamTableIndex ?? 0;
    const parentTeamId = this.normalizeTeamId(parentTeamRow?.caseApplicationTeamId);
    const piTeamId = this.resolvePrincipalTeamIdFromPrj();
    const existingApplication = this.isExistingCaseApplication();

    // First research team row on existing application: principal team already in Oracle.
    if (rowIndex === 0 && piTeamId) {
      this.syncParentTeamId(parentTeamRow, piTeamId);
      return {teamId: piTeamId, isCreateNewTeam: false};
    }

    // This outer row already linked to a persisted team (incl. 2nd+ rows in multi-team forms).
    if (parentTeamId) {
      return {teamId: parentTeamId, isCreateNewTeam: false};
    }

    // Single team row in form: reuse id from globalPrj if present.
    const siblingTeamIds = this.collectPersistedTeamIdsFromTeamTable();
    if (siblingTeamIds.length === 1) {
      const teamId = siblingTeamIds[0];
      this.syncParentTeamId(parentTeamRow, teamId);
      return {teamId, isCreateNewTeam: false};
    }

    if (piTeamId) {
      this.syncParentTeamId(parentTeamRow, piTeamId);
      return {teamId: piTeamId, isCreateNewTeam: false};
    }

    // Row 0 on saved application: Camunda falls back to IS_PRINCIPAL team when id is omitted.
    if (rowIndex === 0 && existingApplication) {
      return {teamId: undefined, isCreateNewTeam: false};
    }

    return {teamId: undefined, isCreateNewTeam: true};
  }

  /** Principal CASE_APPLICATION_TEAM id when the application already has a research team in Oracle. */
  private resolvePrincipalTeamIdFromPrj(): number | undefined {
    const prj = this.host.globalPrj;
    for (const candidate of [prj?.caseApplicationPiTeamId, prj?.caseApplicationTeamId]) {
      const id = this.normalizeTeamId(candidate);
      if (id) {
        return id;
      }
    }
    return undefined;
  }

  private isExistingCaseApplication(): boolean {
    return this.resolveCaseApplicationId() > 0;
  }

  private collectPersistedTeamIdsFromTeamTable(): number[] {
    const {teamTableBlockName, selectedSectionId} = this.host;
    if (!teamTableBlockName || !selectedSectionId) {
      return [];
    }
    const rows = this.host.globalPrj?.[selectedSectionId]?.[teamTableBlockName]?.rows;
    if (!Array.isArray(rows)) {
      return [];
    }
    const ids = rows
      .map((r: any) => this.normalizeTeamId(r?.caseApplicationTeamId))
      .filter((id): id is number => id != null);
    return [...new Set(ids)];
  }

  private syncParentTeamId(parentTeamRow: any | null, teamId: number): void {
    if (parentTeamRow && teamId > 0) {
      parentTeamRow.caseApplicationTeamId = teamId;
    }
  }

  private saveRowToProject(row: any, rowIndex: number): void {
    const {teamTableBlockName, teamTableNestedTableBlockName, teamTableIndex, selectedSectionId, confChange} = this.host;
    if (teamTableBlockName && teamTableNestedTableBlockName) {
      this.host.globalPrj[selectedSectionId][teamTableBlockName].rows[teamTableIndex][teamTableNestedTableBlockName].rows[rowIndex] = row;
    } else {
      this.host.globalPrj[selectedSectionId][confChange.name].rows[rowIndex] = row;
    }
    this.refreshDataService.saveForm({operation: 'Save Without Validation', prj: this.host.globalPrj});
  }

  private showMessage(en: string, fr: string): void {
    const message = this.host.selectedLanguage === 'en' ? en : fr;
    this.snackBar.show('success', message);
  }
}
